import { Video, Mail, Users, Heart, Lock, MessageCircle, DollarSign } from "lucide-react";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { useTranslation } from "react-i18next";

const features = [
    {
        icon: Users,
        title: "Your Unique Profile",
        description: "Create and customize your personal fundraising profile that tells your story and showcases your mission.",
    },
    {
        icon: Video,
        title: "Share Your Journey",
        description: "Upload short videos from the field, add photos, and create compelling stories that connect with supporters.",
    },
    {
        icon: Mail,
        title: "Direct Communication",
        description: "Easily send email updates to your followers and donors. Keep them engaged with your ministry's progress.",
    },
    {
        icon: Heart,
        title: "Smart Donor Engagement",
        description: "Automatic prompts help re-engage donors and encourage continued support with gentle, timely reminders.",
    },
    {
        icon: Lock,
        title: "Flexible Content Control",
        description: "Choose who sees your posts: everyone, followers only, or donors. You control your content access levels.",
    },
    {
        icon: MessageCircle,
        title: "Build Community",
        description: "Engage in meaningful discussions with your supporters through post comments and direct interactions.",
    },
];

const FeaturesSection = () => {
    const { t } = useTranslation();
    const { ref, isVisible } = useScrollAnimation();

    return (
        <section
            ref={ref}
            id="features"
            className={`py-24 bg-background transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                }`}
        >
            <div className="max-w-7xl mx-auto px-6">
                <div className="max-w-3xl mb-16">
                    <span className="inline-block text-sm font-semibold text-primary uppercase tracking-wider mb-3">
                        Built for ministry
                    </span>
                    <h2 className="text-4xl md:text-5xl font-bold text-foreground mb-4 leading-tight">
                        {t('homepage.features.title')}
                    </h2>
                    <p className="text-xl text-muted-foreground">
                        {t('homepage.features.subtitle')}
                    </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 mb-16">
                    {features.map((feature, index) => (
                        <div
                            key={index}
                            className={`group transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
                                }`}
                            style={{ transitionDelay: `${index * 120}ms` }}
                        >
                            <div className="w-12 h-12 rounded-xl bg-accent flex items-center justify-center mb-5">
                                <feature.icon className="w-6 h-6 text-primary" />
                            </div>
                            <h3 className="text-xl font-semibold mb-3 text-foreground">{feature.title}</h3>
                            <p className="text-muted-foreground leading-relaxed">{feature.description}</p>
                        </div>
                    ))}
                </div>

                <div id="how-it-works" className="rounded-3xl overflow-hidden bg-card border border-border shadow-sm">
                    <div className="grid lg:grid-cols-5 gap-0 items-stretch">
                        <div className="lg:col-span-2 relative min-h-[280px] lg:min-h-[400px]">
                            <img
                                src="/images/classroom-lagos.jpg"
                                alt="Children in a classroom learning together"
                                className="absolute inset-0 w-full h-full object-cover"
                                loading="lazy"
                            />
                        </div>
                        <div className="lg:col-span-3 p-8 md:p-12 flex flex-col justify-center">
                            <div className="w-12 h-12 rounded-xl bg-primary flex items-center justify-center mb-5">
                                <DollarSign className="w-6 h-6 text-primary-foreground" />
                            </div>
                            <h3 className="text-2xl md:text-3xl font-bold mb-4 text-foreground leading-tight">Flexible Giving Options</h3>
                            <p className="text-muted-foreground mb-6 leading-relaxed text-lg">
                                Your supporters can choose how they want to give — whether through one-time donations or ongoing monthly support. Make it easy for them to commit to your mission long-term.
                            </p>
                            <div className="flex flex-wrap gap-2">
                                <span className="px-4 py-1.5 bg-accent text-accent-foreground rounded-full text-sm font-medium">One-time Giving</span>
                                <span className="px-4 py-1.5 bg-accent text-accent-foreground rounded-full text-sm font-medium">Monthly Support</span>
                                <span className="px-4 py-1.5 bg-accent text-accent-foreground rounded-full text-sm font-medium">Custom Amounts</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default FeaturesSection;
